import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Heading,
  Loader,
  Flex,
  Text,
  TextField,
  Dropdown,
  Checkbox,
} from '@vibe/core';
import {
  getLeaveApprovers,
  setLeaveApprover,
  bulkSetLeaveApprovers,
  type LeaveApproverMapping,
} from '../../api/settings.api';
import { fetchMondayUsers, type MondayUser } from '../../api/monday.api';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface UserRow {
  mondayUserId: string;
  name: string;
  email: string;
  approverUserId: string | null;
  updatedAt: string | null;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const LeaveApproverPanel: React.FC = () => {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [allUsers, setAllUsers] = useState<MondayUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [bulkApproverId, setBulkApproverId] = useState<string | null>(null);
  const [bulkApproverName, setBulkApproverName] = useState<string | null>(null);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);

  // ─── Load data ──────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [users, mappings] = await Promise.all([
        fetchMondayUsers(),
        getLeaveApprovers(),
      ]);
      setAllUsers(users);

      // Build a lookup of existing approver mappings
      const mappingMap = new Map<string, LeaveApproverMapping>();
      for (const m of mappings) {
        mappingMap.set(m.mondayUserId, m);
      }

      // Merge monday.com users with their approver assignment
      const merged: UserRow[] = users.map((u) => {
        const mapping = mappingMap.get(String(u.id));
        return {
          mondayUserId: String(u.id),
          name: u.name,
          email: u.email,
          approverUserId: mapping?.defaultLeaveApprover ?? null,
          updatedAt: mapping?.updatedAt ?? null,
        };
      });
      setRows(merged);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Approver dropdown options ──────────────────────────────────────────────

  const approverOptions = useMemo(() => {
    const userOpts = allUsers.map((u) => ({
      value: String(u.id),
      label: `${u.name} (${u.email})`,
    }));
    return [{ value: '__none__', label: '-- None --' }, ...userOpts];
  }, [allUsers]);

  // ─── Per-row approver change ────────────────────────────────────────────────

  const handleApproverChange = useCallback(
    async (
      mondayUserId: string,
      option: { value: string; label: string } | null,
    ) => {
      const approverId =
        !option || option.value === '__none__' ? null : option.value;

      setSavingUserId(mondayUserId);
      setError(null);
      try {
        const updated = await setLeaveApprover(mondayUserId, approverId);
        setRows((prev) =>
          prev.map((r) =>
            r.mondayUserId === mondayUserId
              ? {
                  ...r,
                  approverUserId: updated.defaultLeaveApprover,
                  updatedAt: updated.updatedAt,
                }
              : r,
          ),
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to update leave approver';
        setError(message);
      } finally {
        setSavingUserId(null);
      }
    },
    [],
  );

  // ─── Selection ──────────────────────────────────────────────────────────────

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.mondayUserId.includes(q),
    );
  }, [rows, searchQuery]);

  const allFilteredSelected =
    filteredRows.length > 0 &&
    filteredRows.every((r) => selectedUserIds.has(r.mondayUserId));

  const handleSelectAll = useCallback(() => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const r of filteredRows) {
          next.delete(r.mondayUserId);
        }
      } else {
        for (const r of filteredRows) {
          next.add(r.mondayUserId);
        }
      }
      return next;
    });
  }, [filteredRows, allFilteredSelected]);

  const handleToggleSelect = useCallback((mondayUserId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(mondayUserId)) {
        next.delete(mondayUserId);
      } else {
        next.add(mondayUserId);
      }
      return next;
    });
  }, []);

  // ─── Bulk assignment ────────────────────────────────────────────────────────

  const handleBulkApply = useCallback(async () => {
    if (selectedUserIds.size === 0) return;

    const approverId =
      !bulkApproverId || bulkApproverId === '__none__'
        ? null
        : bulkApproverId;

    setIsBulkSaving(true);
    setError(null);
    try {
      const results = await bulkSetLeaveApprovers(
        Array.from(selectedUserIds),
        approverId,
      );

      const resultMap = new Map<string, LeaveApproverMapping>();
      for (const r of results) {
        resultMap.set(r.mondayUserId, r);
      }

      setRows((prev) =>
        prev.map((row) => {
          const result = resultMap.get(row.mondayUserId);
          if (result) {
            return {
              ...row,
              approverUserId: result.defaultLeaveApprover,
              updatedAt: result.updatedAt,
            };
          }
          return row;
        }),
      );
      setSelectedUserIds(new Set());
      setBulkApproverId(null);
      setBulkApproverName(null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to bulk-assign approvers';
      setError(message);
    } finally {
      setIsBulkSaving(false);
    }
  }, [selectedUserIds, bulkApproverId]);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getApproverValue = (approverUserId: string | null) => {
    if (!approverUserId) return null;
    const user = allUsers.find((u) => String(u.id) === approverUserId);
    if (user) {
      return {
        value: String(user.id),
        label: `${user.name} (${user.email})`,
      };
    }
    return { value: approverUserId, label: `User ${approverUserId}` };
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="rp-leave-approver">
      {/* Header */}
      <div className="rp-leave-approver__header">
        <Heading type={Heading.types.H3}>Leave Approvers</Heading>
        <Text
          type={Text.types.TEXT2}
          style={{ color: '#676879', marginTop: 4, marginBottom: 12 }}
        >
          Assign a default leave approver for each user. When assigned, the
          approver will be automatically set and locked in the leave request
          form.
        </Text>
      </div>

      {/* Search filter */}
      <div className="rp-leave-approver__filter-bar">
        <TextField
          size={TextField.sizes.SMALL}
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(value: string) => setSearchQuery(value)}
          className="rp-leave-approver__search"
        />
        <Button
          size={Button.sizes.SMALL}
          kind={Button.kinds.TERTIARY}
          onClick={handleSelectAll}
        >
          {allFilteredSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectedUserIds.size > 0 && (
        <div className="rp-leave-approver__bulk-bar">
          <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
            {selectedUserIds.size} user{selectedUserIds.size > 1 ? 's' : ''}{' '}
            selected
          </Text>
          <Dropdown
            size={Dropdown.sizes.SMALL}
            options={approverOptions}
            value={
              bulkApproverId
                ? approverOptions.find((o) => o.value === bulkApproverId) ??
                  null
                : null
            }
            onChange={(option) => {
              if (!option) {
                setBulkApproverId(null);
                setBulkApproverName(null);
                return;
              }
              const selected = option as { value: string; label: string };
              setBulkApproverId(selected.value);
              setBulkApproverName(selected.label);
            }}
            placeholder="Select approver..."
            clearable
            className="rp-leave-approver__dropdown"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          <Button
            size={Button.sizes.SMALL}
            onClick={handleBulkApply}
            loading={isBulkSaving}
            disabled={!bulkApproverId}
          >
            Apply
          </Button>
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.TERTIARY}
            onClick={() => {
              setSelectedUserIds(new Set());
              setBulkApproverId(null);
              setBulkApproverName(null);
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="rp-leave-approver__error-banner">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="rp-leave-approver__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rp-leave-approver__empty">
          <Text>No users found.</Text>
        </div>
      ) : (
        <table className="rp-settings-table">
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th>User</th>
              <th>Email</th>
              <th>Leave Approver</th>
              <th style={{ width: 120 }}>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => {
              const isSelected = selectedUserIds.has(row.mondayUserId);
              const isSaving = savingUserId === row.mondayUserId;

              return (
                <tr key={row.mondayUserId}>
                  <td>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleSelect(row.mondayUserId)}
                      ariaLabel={`Select ${row.name}`}
                    />
                  </td>
                  <td>
                    <Text
                      type={Text.types.TEXT1}
                      weight={Text.weights.MEDIUM}
                    >
                      {row.name}
                    </Text>
                  </td>
                  <td>
                    <Text type={Text.types.TEXT2}>{row.email}</Text>
                  </td>
                  <td>
                    <Flex align={Flex.align.CENTER} gap={Flex.gaps.XS}>
                      <Dropdown
                        size={Dropdown.sizes.SMALL}
                        options={approverOptions}
                        value={getApproverValue(row.approverUserId)}
                        onChange={(option) =>
                          handleApproverChange(
                            row.mondayUserId,
                            option as {
                              value: string;
                              label: string;
                            } | null,
                          )
                        }
                        placeholder="Select approver..."
                        clearable
                        className="rp-leave-approver__dropdown"
                        disabled={isSaving}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {isSaving && <Loader size={Loader.sizes.XS} />}
                    </Flex>
                  </td>
                  <td>
                    <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                      {formatDate(row.updatedAt)}
                    </Text>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default LeaveApproverPanel;
